import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CourseList from "@/pages/CourseList";
import Chapter from "@/pages/Chapter";
import FinalExam from "@/pages/FinalExam";
import Practice from "@/pages/Practice";
import Certificate from "@/pages/Certificate";
import Course1120List from "@/pages/Course1120List";
import Course1120Chapter from "@/pages/Course1120Chapter";
import Course1120Practice from "@/pages/Course1120Practice";
import Course1120Exam from "@/pages/Course1120Exam";
import Course1120Certificate from "@/pages/Course1120Certificate";
import Course1040List from "@/pages/Course1040List";
import Course1040Chapter from "@/pages/Course1040Chapter";
import Course1040Practice from "@/pages/Course1040Practice";
import Admin from "@/pages/Admin";
import Bookmarks from "@/pages/Bookmarks";
import Questions from "@/pages/Questions";
import Students from "@/pages/Students";
import StudentDetail from "@/pages/StudentDetail";
import Digest from "@/pages/Digest";
import AdminPayments from "@/pages/AdminPayments";
import AdminPasswordResets from "@/pages/AdminPasswordResets";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminIntake from "@/pages/AdminIntake";
import AdminAmendments from "@/pages/AdminAmendments";
import AdminAppointments from "@/pages/AdminAppointments";
import Home from "@/pages/Home";
import Preview from "@/pages/Preview";
import MeetInstructor from "@/pages/MeetInstructor";
import WhiteLabel from "@/pages/WhiteLabel";
import Refresher1120 from "@/pages/Refresher1120";
import Course1040Landing from "@/pages/Course1040Landing";
import AfspBundle from "@/pages/AfspBundle";
import AfspPrep from "@/pages/AfspPrep";
import Clients from "@/pages/Clients";
import BecomeATaxPro from "@/pages/BecomeATaxPro";
import TaxPros from "@/pages/TaxPros";
import AfspCertificate from "@/pages/AfspCertificate";
import VerifyCertificate from "@/pages/VerifyCertificate";
import AfspExamSim from "@/pages/AfspExamSim";
import AdminAfspCohort from "@/pages/AdminAfspCohort";
import AffiliateApply from "@/pages/AffiliateApply";
import AffiliatePaperwork from "@/pages/AffiliatePaperwork";
import AdminAffiliateContracts from "@/pages/AdminAffiliateContracts";
import AdminAffiliatePaperwork from "@/pages/AdminAffiliatePaperwork";
import AdminTenants from "@/pages/AdminTenants";
import AdminTeam from "@/pages/AdminTeam";
import TenantSettings from "@/pages/TenantSettings";
import TeamInvite from "@/pages/TeamInvite";
import AiHelp from "@/pages/AiHelp";
import FloatingAiChat from "@/components/FloatingAiChat";
import WhiteLabelOnboarding from "@/pages/WhiteLabelOnboarding";
import AdminWhiteLabel from "@/pages/AdminWhiteLabel";
import AdminInstructorBio from "@/pages/AdminInstructorBio";
import { DemoOverview, DemoBranding, DemoStudents, DemoPayments, DemoPasswordResets, DemoIntake, DemoAmendments, DemoAppointments, DemoDigest } from "@/pages/DemoScreens";
import Intake from "@/pages/Intake";
import Amend from "@/pages/Amend";
import Book from "@/pages/Book";
import Reschedule from "@/pages/Reschedule";
import Services from "@/pages/Services";
import ClientPortal from "@/pages/ClientPortal";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuthCallback from "@/pages/AuthCallback";
import WelcomeBubble from "@/components/WelcomeBubble";
import TawkLoader from "@/components/TawkLoader";
import "@/App.css";

const Protected = ({ children, adminOnly }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/dashboard" replace />;
  return children;
};

const PublicOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10">Loading…</div>;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/dashboard"} replace />;
  return children;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <BrandingProvider>
          <Toaster richColors position="top-right" />
          <WelcomeBubble />
          <TawkLoader />
          <FloatingAiChat />
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/preview" element={<Preview />} />
            <Route path="/meet" element={<MeetInstructor />} />
            <Route path="/white-label" element={<WhiteLabel />} />
            <Route path="/1120-refresher" element={<Refresher1120 />} />
            <Route path="/1040-course" element={<Course1040Landing />} />
            <Route path="/become-a-tax-pro" element={<BecomeATaxPro />} />
            <Route path="/tax-pros" element={<TaxPros />} />
            <Route path="/clients" element={<Clients />} />
            <Route path="/afsp-bundle" element={<AfspBundle />} />
            <Route path="/afsp-prep" element={<Protected><AfspPrep /></Protected>} />
            <Route path="/afsp-exam-sim" element={<Protected><AfspExamSim /></Protected>} />
            <Route path="/afsp-certificate" element={<Protected><AfspCertificate /></Protected>} />
            <Route path="/verify/:certId" element={<VerifyCertificate />} />
            <Route path="/white-label/onboarding" element={<Protected><WhiteLabelOnboarding /></Protected>} />
            <Route path="/demo" element={<DemoOverview />} />
            <Route path="/demo/branding" element={<DemoBranding />} />
            <Route path="/demo/students" element={<DemoStudents />} />
            <Route path="/demo/payments" element={<DemoPayments />} />
            <Route path="/demo/password-resets" element={<DemoPasswordResets />} />
            <Route path="/demo/intake" element={<DemoIntake />} />
            <Route path="/demo/amendments" element={<DemoAmendments />} />
            <Route path="/demo/appointments" element={<DemoAppointments />} />
            <Route path="/demo/digest" element={<DemoDigest />} />
            <Route path="/intake" element={<Intake />} />
            <Route path="/amend" element={<Amend />} />
            <Route path="/book" element={<Book />} />
            <Route path="/reschedule/:token" element={<Reschedule />} />
            <Route path="/services" element={<Services />} />
            <Route path="/client-portal" element={<ClientPortal />} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
            <Route path="/forgot-password" element={<PublicOnly><ForgotPassword /></PublicOnly>} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/dashboard" element={<Protected><Dashboard /></Protected>} />
            {/* Mastering Form 1120 (Corporate) — public preview + gated full course.
                These MUST come BEFORE the /course/:id catch-all below. */}
            <Route path="/course/1120" element={<Course1120List />} />
            <Route path="/course/1120/chapter/:id" element={<Course1120Chapter />} />
            <Route path="/course/1120/practice/:id" element={<Course1120Practice />} />
            <Route path="/course/1120/exam" element={<Protected><Course1120Exam /></Protected>} />
            <Route path="/course/1120/certificate" element={<Protected><Course1120Certificate /></Protected>} />
            {/* Master the 1040 — public preview (chapters 1-3 free) */}
            <Route path="/course/1040" element={<Course1040List />} />
            <Route path="/course/1040/chapter/:id" element={<Course1040Chapter />} />
            <Route path="/course/1040/practice/:id" element={<Course1040Practice />} />
            <Route path="/course" element={<Protected><CourseList /></Protected>} />
            <Route path="/course/:id" element={<Protected><Chapter /></Protected>} />
            <Route path="/exam" element={<Protected><FinalExam /></Protected>} />
            <Route path="/practice" element={<Protected><Practice /></Protected>} />
            <Route path="/certificate" element={<Protected><Certificate /></Protected>} />
            <Route path="/bookmarks" element={<Protected><Bookmarks /></Protected>} />
            <Route path="/questions" element={<Protected><Questions /></Protected>} />
            <Route path="/admin" element={<Protected adminOnly><Admin /></Protected>} />
            <Route path="/admin/dashboard" element={<Protected adminOnly><AdminDashboard /></Protected>} />
            <Route path="/admin/students" element={<Protected adminOnly><Students /></Protected>} />
            <Route path="/admin/students/:id" element={<Protected adminOnly><StudentDetail /></Protected>} />
            <Route path="/admin/digest" element={<Protected adminOnly><Digest /></Protected>} />
            <Route path="/admin/payments" element={<Protected adminOnly><AdminPayments /></Protected>} />
            <Route path="/admin/afsp-cohort" element={<Protected adminOnly><AdminAfspCohort /></Protected>} />
            <Route path="/affiliate/apply" element={<AffiliateApply />} />
            <Route path="/affiliate/paperwork" element={<AffiliatePaperwork />} />
            <Route path="/admin/affiliate-contracts" element={<Protected adminOnly><AdminAffiliateContracts /></Protected>} />
            <Route path="/admin/affiliate-paperwork" element={<Protected adminOnly><AdminAffiliatePaperwork /></Protected>} />
            <Route path="/admin/tenants" element={<Protected adminOnly><AdminTenants /></Protected>} />
            <Route path="/admin/team" element={<Protected adminOnly><AdminTeam /></Protected>} />
            <Route path="/team-invite/:code" element={<TeamInvite />} />
            <Route path="/ai-help" element={<AiHelp />} />
            <Route path="/tenant/settings" element={<Protected><TenantSettings /></Protected>} />
            <Route path="/admin/password-resets" element={<Protected adminOnly><AdminPasswordResets /></Protected>} />
            <Route path="/admin/intake" element={<Protected adminOnly><AdminIntake /></Protected>} />
            <Route path="/admin/amendments" element={<Protected adminOnly><AdminAmendments /></Protected>} />
            <Route path="/admin/appointments" element={<Protected adminOnly><AdminAppointments /></Protected>} />
            <Route path="/admin/instructor-bio" element={<Protected adminOnly><AdminInstructorBio /></Protected>} />
            <Route path="/admin/white-label" element={<Protected adminOnly><AdminWhiteLabel /></Protected>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrandingProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
